'use client'

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from 'react'
import type { Locale } from '@/lib/i18n/types'
import type { MessageTree } from '@/lib/i18n/translate'
import { createTranslator } from '@/lib/i18n/translate'

type I18nContextValue = {
  locale: Locale
  t: (key: string) => string
  setLocale: (locale: Locale) => void
}

const I18nContext = createContext<I18nContextValue | null>(null)

export function I18nProvider({
  locale,
  messages,
  children,
}: {
  locale: Locale
  messages: MessageTree
  children: ReactNode
}) {
  const translate = useMemo(() => createTranslator(messages), [messages])

  const setLocale = useCallback(
    (next: Locale) => {
      if (next === locale) return
      const path =
        window.location.pathname +
        window.location.search +
        window.location.hash
      const qs = new URLSearchParams({ return: path })
      window.location.href = `/api/locale/${next}?${qs.toString()}`
    },
    [locale],
  )

  const value = useMemo(
    () => ({
      locale,
      t: translate,
      setLocale,
    }),
    [locale, translate, setLocale],
  )

  return (
    <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
  )
}

export function useI18n() {
  const ctx = useContext(I18nContext)
  if (!ctx) {
    throw new Error('useI18n must be used within I18nProvider')
  }
  return ctx
}
